import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Typography,
} from "@material-ui/core";

import { formatAsCLP, formatValorPlan } from "src/utils/formatNumber";

const MARGEN_REDONDEO = 2;

export default function FacturacionSection({ formik }) {
  const { values, errors, touched, setFieldValue, handleBlur } = formik;
  const meta = values._meta;
  const { tipoMonedaTarea, valorTipoMoneda } = meta;
  const esMonedaExtranjera = tipoMonedaTarea !== "PESOS";

  const porcentajeMaximoDisponible =
    meta.totalPagarCLP > 0
      ? (meta.totalRestanteCLP / meta.totalPagarCLP) * 100
      : 0;

  const calcularMontoDesdePorcentaje = (porcNum) => {
    const calculado = Math.round(meta.totalPagarCLP * (porcNum / 100));
    const diferencia = Math.abs(calculado - meta.totalRestanteCLP);
    return calculado > meta.totalRestanteCLP && diferencia <= MARGEN_REDONDEO
      ? meta.totalRestanteCLP
      : calculado;
  };

  const handleChangeAbono = (event) => {
    const nuevoAbono = event.target.value;
    setFieldValue("abono", nuevoAbono);
    if (nuevoAbono === "NO") {
      setFieldValue("porcentaje", "");
      setFieldValue("montoFacturar", meta.totalRestanteCLP);
    } else {
      const porcNum = Number(values.porcentaje);
      setFieldValue(
        "montoFacturar",
        porcNum > 0 ? calcularMontoDesdePorcentaje(porcNum) : 0,
      );
    }
  };

  const handleChangePorcentaje = (event) => {
    const valor = event.target.value;
    setFieldValue("porcentaje", valor);
    if (values.abono === "SI") {
      const porcNum = Number(valor);
      setFieldValue(
        "montoFacturar",
        porcNum > 0 ? calcularMontoDesdePorcentaje(porcNum) : 0,
      );
    }
  };

  const montoIgualRestante =
    Number(values.montoFacturar) === Number(meta.totalRestanteCLP) &&
    meta.totalRestanteCLP > 0;

  const equivalenteEnMonedaOriginal =
    esMonedaExtranjera && valorTipoMoneda > 0 && values.montoFacturar > 0
      ? formatValorPlan(
          Number(values.montoFacturar) / valorTipoMoneda,
          tipoMonedaTarea,
        )
      : null;

  const renderFila = (label, valorOriginal, valorCLP) => (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="flex-start"
      sx={{ py: 0.75 }}
    >
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        {label}
      </Typography>
      <Box sx={{ textAlign: "right" }}>
        {esMonedaExtranjera ? (
          <>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {formatValorPlan(valorOriginal, tipoMonedaTarea)}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", fontSize: 12 }}
            >
              &asymp; {formatAsCLP(valorCLP)}
            </Typography>
          </>
        ) : (
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            {formatAsCLP(valorCLP)}
          </Typography>
        )}
      </Box>
    </Stack>
  );

  const renderFilaCLP = (label, valorCLP, valorColor) => (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      sx={{ py: 0.75 }}
    >
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        {label}
      </Typography>
      <Typography
        variant="body1"
        sx={{ fontWeight: 500, color: valorColor || "text.primary" }}
      >
        {formatAsCLP(valorCLP)}
      </Typography>
    </Stack>
  );

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6" sx={{ mb: 2 }}>
          💰 Facturación
        </Typography>

        <Stack divider={<Divider flexItem />} spacing={0}>
          {renderFila(
            "Total tarea",
            meta.valorTareaOriginal,
            Math.round(meta.valorTareaOriginal * valorTipoMoneda),
          )}

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ py: 0.75 }}
          >
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Descuento
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {meta.descuentoPorcentaje}%
            </Typography>
          </Stack>

          {renderFila(
            "Total a pagar",
            meta.totalPagarOriginal,
            meta.totalPagarCLP,
          )}

          {esMonedaExtranjera && (
            <Box sx={{ py: 0.75 }}>
              <Chip
                size="small"
                label={`Valor ${tipoMonedaTarea} hoy: ${formatAsCLP(
                  valorTipoMoneda,
                )}`}
                sx={{
                  bgcolor: "rgba(255, 149, 0, 0.12)",
                  color: "#b26a00",
                  fontWeight: 500,
                }}
              />
            </Box>
          )}

          {renderFilaCLP("Facturado", meta.totalFacturadoCLP)}
          {renderFilaCLP(
            "Restante",
            meta.totalRestanteCLP,
            meta.totalRestanteCLP > 0 ? "primary.main" : "text.primary",
          )}
        </Stack>

        <Divider sx={{ my: 2 }} />

        <FormControl component="fieldset" sx={{ mb: 1 }}>
          <FormLabel component="legend">¿Abonar porcentaje?</FormLabel>
          <RadioGroup
            row
            name="abono"
            value={values.abono}
            onChange={handleChangeAbono}
          >
            <FormControlLabel value="SI" control={<Radio />} label="Sí" />
            <FormControlLabel value="NO" control={<Radio />} label="No" />
          </RadioGroup>
        </FormControl>

        {values.abono === "SI" && (
          <FormControl
            fullWidth
            sx={{ mb: 2 }}
            error={touched.porcentaje && !!errors.porcentaje}
          >
            <InputLabel htmlFor="porcentaje-input">Porcentaje</InputLabel>
            <OutlinedInput
              id="porcentaje-input"
              name="porcentaje"
              type="number"
              value={values.porcentaje}
              onChange={handleChangePorcentaje}
              onBlur={handleBlur}
              inputProps={{ onWheel: (e) => e.target.blur() }}
              startAdornment={
                <InputAdornment position="start">%</InputAdornment>
              }
              label="Porcentaje"
            />
            <FormHelperText>
              {touched.porcentaje && errors.porcentaje
                ? errors.porcentaje
                : `Máximo disponible: ${porcentajeMaximoDisponible.toFixed(2)}%`}
            </FormHelperText>
          </FormControl>
        )}

        <FormControl
          fullWidth
          sx={{ mb: 2 }}
          error={touched.montoFacturar && !!errors.montoFacturar}
        >
          <InputLabel htmlFor="monto-input">Monto a emitir (CLP)</InputLabel>
          <OutlinedInput
            id="monto-input"
            name="montoFacturar"
            type="number"
            value={values.montoFacturar}
            onChange={(e) => setFieldValue("montoFacturar", e.target.value)}
            onBlur={handleBlur}
            disabled={values.abono === "SI"}
            inputProps={{ onWheel: (e) => e.target.blur() }}
            startAdornment={<InputAdornment position="start">$</InputAdornment>}
            label="Monto a emitir (CLP)"
          />
          <FormHelperText>
            {touched.montoFacturar && errors.montoFacturar
              ? errors.montoFacturar
              : montoIgualRestante
                ? "Se facturará el total restante; la tarea quedará marcada como facturada completamente."
                : equivalenteEnMonedaOriginal
                  ? `\u2248 ${equivalenteEnMonedaOriginal} al valor de hoy`
                  : " "}
          </FormHelperText>
        </FormControl>

        <FormControl fullWidth>
          <InputLabel id="formatoPago-label">Forma de pago</InputLabel>
          <Select
            labelId="formatoPago-label"
            id="formatoPago"
            name="formatoPago"
            value={values.formatoPago}
            label="Forma de pago"
            onChange={(e) => setFieldValue("formatoPago", e.target.value)}
          >
            <MenuItem value="1">Contado</MenuItem>
            <MenuItem value="2">Crédito</MenuItem>
            <MenuItem value="3">Sin costo (entrega gratuita)</MenuItem>
          </Select>
        </FormControl>
      </CardContent>
    </Card>
  );
}
